import type { ReactNode } from "react";
import { FC, useEffect } from "react";
import { trackJourneyStepViewed, trackJourneyView } from "@/data/analytics/ga4";
import { JourneySchema, StepSchema, StepType, PipeType, StandardPipe, JunctionType } from "@schema-types/types/JourneySchema";
import { FormStepSubmitHandler } from "@schema-types/types";
import "../styles/tailwind.css";
import { FormStep } from "./Steps/FormStep/FormStep";
import { StepProgress } from "./StepProgress/StepProgress";
import { ContentStep } from "./Steps/ContentStep";
import { FinalStep } from "./Steps/FinalStep";
import { FinalSignupStep } from "./Steps/FinalSignupStep";
import { KickoutStep } from "./Steps/KickoutStep";
import type { EnquiryStepData } from "@/data/enquiries/types";
import { cn } from "@shadcn/lib/utils";
import type { TurnstileActionFeedbackProps } from "./_common/TurnstileActionFeedback";

export type { EnquiryStepData };

type StepRendererProps = {
  journey: JourneySchema;
  step: StepSchema;
  journeyVersion?: string;
  data?: Record<string, any>;
  journeyData: EnquiryStepData[];
  onNextClick: FormStepSubmitHandler;
  onBackClick: () => void;
  isLoading: boolean;
  showProgress: boolean;
  hasExistingData?: boolean;
  enquiryId?: string;
  renderBeforeActions?: ReactNode;
  disableNextUntilTurnstile?: boolean;
  turnstileFeedback?: TurnstileActionFeedbackProps;
  staffEnquiryPreviewMode?: boolean;
  staffEnquiryPreviewSaveDisabled?: boolean;
  readOnly?: boolean;
  compactLayout?: boolean;
  evaluationTimeZoneId?: string | null;
};

const getFrontEndStepProgress = (journey: JourneySchema, currentStepId: string) => {
  const standardPipes = journey.stepPipeline.filter(
    (pipe): pipe is StandardPipe => pipe.type === PipeType.Standard
  );

  if (!standardPipes.length) {
    return { orderedFrontEndPipes: [] as StandardPipe[], currentStepPosition: 0 };
  }

  const stepsById = new Map(journey.steps.map((step) => [step.id, step]));
  const pipeById = new Map(standardPipes.map((pipe) => [pipe.id, pipe]));
  const incomingMap = new Set<string>();
  const directLinkMap = new Map<string, string>();

  for (const pipe of journey.stepPipeline) {
    const directJunction = pipe.junctions?.find((junction) => junction.type === JunctionType.Direct);
    if (directJunction && pipeById.has(directJunction.targetPipeId)) {
      incomingMap.add(directJunction.targetPipeId);
      if (pipe.type === PipeType.Standard) {
        directLinkMap.set(pipe.id, directJunction.targetPipeId);
      }
    }
  }

  const firstPipe = journey.stepPipeline[0];
  const startPipe =
    firstPipe?.type === PipeType.Start
      ? (standardPipes.find((p) => p.id === firstPipe.junctions?.find((j) => j.type === JunctionType.Direct)?.targetPipeId) ?? standardPipes[0])
      : (standardPipes.find((pipe) => !incomingMap.has(pipe.id)) ?? standardPipes[0]);

  const orderedFrontEndPipes: StandardPipe[] = [];
  const visited = new Set<string>();
  let current: StandardPipe | undefined = startPipe;

  while (current && !visited.has(current.id)) {
    const step = stepsById.get(current.targetId);
    if (step && (step.type === StepType.Form || step.type === StepType.Content)) {
      orderedFrontEndPipes.push(current);
    }
    visited.add(current.id);
    const nextId = directLinkMap.get(current.id);
    current = nextId ? pipeById.get(nextId) : undefined;
  }

  const currentStepPosition =
    orderedFrontEndPipes.findIndex((pipe) => pipe.targetId === currentStepId) + 1;

  return {
    orderedFrontEndPipes,
    currentStepPosition: currentStepPosition > 0 ? currentStepPosition : 0,
  };
};

export const StepRenderer: FC<StepRendererProps> = (props) => {
  const { orderedFrontEndPipes, currentStepPosition } = getFrontEndStepProgress(
    props.journey,
    props.step.id
  );
  const totalSteps = orderedFrontEndPipes.length;
  const currentStep = currentStepPosition;

  const shouldShowProgress = props.showProgress && totalSteps > 0 && currentStep > 0;

  useEffect(() => {
    if (props.compactLayout) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [props.step.id, props.compactLayout]);

  useEffect(() => {
    const stepDisplayId = props.step.displayId ?? props.step.id;
    trackJourneyView(stepDisplayId);
    trackJourneyStepViewed(stepDisplayId);
  }, [props.step.id, props.step.displayId]);

  return (
    <div className={cn(!props.compactLayout && "min-h-screen")}>
      {shouldShowProgress && (
        <div className="border-b border-gray-200 bg-white">
          <div className="mx-auto w-full max-w-3xl px-4 py-3 sm:px-6 sm:py-4">
            <div className="mx-auto w-full">
              <StepProgress
                totalSteps={totalSteps}
                currentStep={currentStep}
                title={props.step.title}
                description={props.step.description}
              />
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto w-full max-w-3xl px-0 mt-0 pb-6 sm:mt-4 sm:pb-8">
        <div className="sm:rounded-lg sm:border sm:border-gray-200 bg-white p-4 px-6 pb-6 shadow-sm sm:p-8 sm:pb-8">
          <div className="mx-auto w-full max-w-3xl">
            {props.step.type === StepType.Form && (
              <FormStep
                key={props.step.id}
                {...props.step}
                data={props.data}
                journey={props.journey}
                journeyData={props.journeyData}
                onSubmit={props.onNextClick}
                onBack={props.onBackClick}
                hasExistingData={props.hasExistingData}
                enquiryId={props.enquiryId}
                renderBeforeActions={props.renderBeforeActions}
                disableNextUntilTurnstile={props.disableNextUntilTurnstile}
                turnstileFeedback={props.turnstileFeedback}
                staffEnquiryPreviewMode={props.staffEnquiryPreviewMode}
                staffEnquiryPreviewSaveDisabled={props.staffEnquiryPreviewSaveDisabled}
                readOnly={props.readOnly}
                evaluationTimeZoneId={props.evaluationTimeZoneId}
              />
            )}
            {props.step.type === StepType.Content && (
              <ContentStep
                id={props.step.id}
                hideNextButton={!!props.readOnly}
                onSubmit={props.onNextClick}
                journeyId={props.journey.id}
                version={props.journeyVersion ?? "dev"}
                renderBeforeActions={props.renderBeforeActions}
                disableNextUntilTurnstile={props.disableNextUntilTurnstile}
                turnstileFeedback={props.turnstileFeedback}
              />
            )}
            {props.step.type === StepType.Final && (
              <FinalStep title={props.step.title} description={props.step.description} />
            )}
            {props.step.type === StepType.FinalSignup && (
              <FinalSignupStep
                title={props.step.title}
                description={props.step.description}
                enquiryId={props.enquiryId}
                readOnly={props.readOnly}
              />
            )}
            {props.step.type === StepType.Kickout && (
              <KickoutStep
                title={props.step.title}
                description={props.step.description}
                phoneNumber={props.step.phoneNumber}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
